import React, { cloneElement, ReactElement } from 'react'

import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import { ArcadeIcon } from '@arcade/web/icons/react'

import { ThemeSpacing } from '../../theme/spacing'
import { Avatar } from '../Avatar/Avatar'
import { Flex, FullWidthFlex } from '../layout/Flex'
import Text from '../Text/Text'

export interface BaseCellAsset {
  Icon?: ArcadeIcon
  avatar?: ReactElement
}

export interface BaseCellProps {
  label: string
  body?: React.ReactNode
  /**
   * Optional: Asset to display on the left side of the cell, i.e. an icon or an avatar.
   */
  asset?: BaseCellAsset
  /**
   * Optional: Accessory to display on the right side of the cell.
   */
  accessory?: ReactElement
  disabled?: boolean
  cellTestId?: string
  headerSpacing?: ThemeSpacing[keyof ThemeSpacing]
}

interface TestIdProps {
  'data-testid'?: string
}

const DISABLED_OPACITY = 0.4
const Opacity = styled.div<{ opacity?: number }>`
  opacity: ${props => props.opacity};
`

/**
 * Asset styles (icon or avatar)
 */
const getIcon = (Icon: ArcadeIcon, disabled: boolean, cellTestId?: string) => (
  <Icon
    size={24}
    variant={disabled ? 'disabled' : 'standard'}
    data-testid={cellTestId ? `cell-icon-${cellTestId}` : 'cell-icon'}
  />
)

const IconWrapper = styled.div`
  height: 1.5rem;
  width: 1.5rem;
`

const BaseCell = React.forwardRef<HTMLDivElement, BaseCellProps & TestIdProps>((props, ref) => {
  const { spacing } = useTheme()
  const {
    label,
    body,
    asset,
    accessory,
    disabled = false,
    'data-testid': baseCellTestId,
    cellTestId,
    headerSpacing = spacing.empty,
  } = props

  /**
   * if data-testid is directly provided, use it: baseCellTestId. Otherwise, if a cellTestId is provided/passed down from an outer component, use that.
   */
  const testId = baseCellTestId || cellTestId

  //  `avatar` can be a CDS Avatar or a custom component. If it's a CDS Avatar, use the `disabled` field. For custom components, simulate disabled state with opacity.
  const renderAvatar = () => {
    if (asset?.avatar?.type === Avatar) {
      return cloneElement(asset.avatar, { disabled })
    } else if (asset?.avatar) {
      return <Opacity opacity={disabled ? DISABLED_OPACITY : undefined}>{asset?.avatar}</Opacity>
    }
    return null
  }

  const renderAccessory = () => (accessory ? cloneElement(accessory, { disabled }) : null)

  const getAlignment = () => {
    if (!body && (asset?.avatar || asset?.Icon)) {
      return 'center'
    }
    return asset?.avatar ? 'flex-start' : 'initial'
  }

  return (
    <Flex {...props} alignItems={getAlignment()} gap={spacing.m} ref={ref} data-testid={testId}>
      {/* The div allows the asset to maintain its size in a flex box */}
      {asset?.Icon ? (
        <IconWrapper>{asset?.Icon && getIcon(asset.Icon, disabled, testId)}</IconWrapper>
      ) : asset?.avatar ? (
        <div>{renderAvatar()}</div>
      ) : null}
      <FullWidthFlex flexDirection="column" gap={headerSpacing}>
        <Text typography="label" color={disabled ? 'cellLabelDisabled' : 'cellLabelDefault'}>
          {label}
        </Text>
        {body && (
          <Text typography="cellBody" color={disabled ? 'cellBodyDisabled' : 'cellBodyDefault'}>
            {body}
          </Text>
        )}
      </FullWidthFlex>
      {accessory ? <div>{renderAccessory()}</div> : null}
    </Flex>
  )
})

export default BaseCell
